import type { LoggerAdapter } from '@signage/player-core';
import { getFile, putFile } from './idb.js';

/**
 * Logs to the browser console and keeps the most recent lines in storage, so a display that
 * misbehaved overnight still has its story the next morning.
 */
const LOG_PATH = 'logs/player.log';
const KEEP_LINES = 2000;
const SAVE_EVERY_MS = 10_000;

export class WebLogger implements LoggerAdapter {
  private lines: string[] = [];
  private dirty = false;

  constructor() {
    void getFile(LOG_PATH)
      .then(async (file) => {
        if (!file) return;
        const previous = (await file.blob.text()).split('\n').filter(Boolean);
        this.lines = [...previous, ...this.lines].slice(-KEEP_LINES);
      })
      .catch(() => {});
    setInterval(() => void this.save(), SAVE_EVERY_MS);
  }

  debug(message: string, context?: Record<string, unknown>): void {
    this.write('debug', message, context);
  }
  info(message: string, context?: Record<string, unknown>): void {
    this.write('info', message, context);
  }
  warn(message: string, context?: Record<string, unknown>): void {
    this.write('warn', message, context);
  }
  error(message: string, context?: Record<string, unknown>): void {
    this.write('error', message, context);
  }

  async save(): Promise<void> {
    if (!this.dirty) return;
    this.dirty = false;
    await putFile(LOG_PATH, new Blob([this.lines.join('\n')], { type: 'text/plain' })).catch(() => {});
  }

  private write(level: 'debug' | 'info' | 'warn' | 'error', message: string, context?: Record<string, unknown>): void {
    const line = `${new Date().toISOString()} [${level}] ${message}${context ? ` ${safeJson(context)}` : ''}`;
    // eslint-disable-next-line no-console
    console[level](line);
    if (level === 'debug') return;
    this.lines.push(line);
    if (this.lines.length > KEEP_LINES) this.lines.splice(0, this.lines.length - KEEP_LINES);
    this.dirty = true;
  }
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return '[unserialisable]';
  }
}
